<template>
  <div class="details">
    <MyHeader
      :cover="head.cover"
      :className="head.className"
      :duty="head.duty"
      :dateRange="head.dateRange"
      :courseName="head.courseName"
    />

    <div class="tab">
      <TabBar :menu="menu" :menuCheckedIdx.sync="menuCheckedIdx" />
    </div>
    <main class="focus" v-show="menuCheckedIdx === 0">
      <!-- 学习计划 -->
      <div class="study" ref="study">
        <div
          class="module"
          v-for="(val, idx) in studyPlan.list"
          :key="idx + val.name"
        >
          <div class="module-name" v-if="val.type && studyPlan.isMode">{{ val.name }}</div>
          <div class="list-item" v-for="(item, index) in val.card" :key="index">
            <StudyPlanList
              :type="val.type"
              :date="item.date"
              :name="item.name"
              :isUnlock="item.isUnlock"
              :contentId="item.contentId"
              :seriesId="studyPlan.series_id"
              :planList="item.planList"
            />
          </div>
        </div>
      </div>
    </main>
    <!-- 班级学员 -->
    <main class="focus" v-show="menuCheckedIdx === 1">
      <ClassStudents :classId="classId" :show="menuCheckedIdx === 1" />
    </main>
    <!-- 打卡列表 -->
    <main class="focus" v-show="menuCheckedIdx === 2">
      <ClockInList :classId="classId" :show="menuCheckedIdx === 2" />
    </main>
  </div>
</template>

<script>
import { getClassDetails } from '@/api/clockin';
import {
  defineComponent,
  ref,
  onMounted,
  nextTick,
} from '@vue/composition-api';
import MyHeader from './components/myHeader.vue';
import TabBar from './components/TabBar.vue';
import StudyPlanList from './components/StudyPlanList.vue';
import ClassStudents from './components/ClassStudents.vue';
import ClockInList from './components/ClockInList.vue';

export default defineComponent({
  components: {
    MyHeader,
    TabBar,
    StudyPlanList,
    ClassStudents,
    ClockInList,
  },
  props: {
    id: String,
  },
  setup(props) {
    const classId = Number(props.id);

    const menu = {
      0: '学习计划',
      1: '班级学员',
      2: '打卡列表',
    };
    const menuCheckedIdx = ref(0);

    /**
     * @description 获取课程封面、课程名、职务、时间范围、班级名和学习计划相关数据
     * @param {number} id 班级ID
     * @returns {promise} {head, series_id, list, moduleIdx: 当前日期的课程所处模块索引, cardIdx: 课程索引}
     */
    const getDetailsAndPlan = id => new Promise((resolve, reject) => {
      getClassDetails(id)
        .then((data) => {
          const backVal = {
            series_id: data.series_id,
            moduleIdx: -1,
            cardIdx: -1,
          };

          backVal.head = {
            cover: data.teacher_image,
            className: data.name,
            duty: data.duty_desc,
            dateRange: {
              begDate: data.training_date.replace(/[-]/g, '.'),
              endDate: data.finish_date.replace(/[-]/g, '.'),
            },
            courseName: data.series_name,
          };

          const { modules } = data.learn_plan;

          // 0 未开启模块模式，1 开启
          backVal.isMode = data.learn_plan.module_mode;

          // 只保存需要的数据，同时将多层嵌套结构转换为 => [[{}], [{}]]。
          backVal.list = modules.map((item, index) => {
            const { name, type } = item;

            const card = item.contents.map((val, idx) => {
              const planList = val.details.map(ele => ({
                typeName: ele.type_name,
                name: ele.name,
                lessonType: ele.lesson_type,
                objectId: ele.object_id,
              }));

              const date = val.unlock_time
                ? val.unlock_time.substr(0, 10).replace(/-/g, '.')
                : '';

              if (date !== '') {
                // 保存当前日期的课程的模块索引和模块内部的小模块位置
                const now = new Date();

                // ios系统里：new Date(2021/09/11) 只支持/符号。
                const newDate = date.replace(/[.-]/g, '/');
                const myDate = new Date(newDate);

                if (
                  now.toLocaleDateString() === myDate.toLocaleDateString()
                ) {
                  backVal.moduleIdx = index;
                  backVal.cardIdx = idx;
                }
              }

              return {
                contentId: val.id,
                isUnlock: Boolean(val.is_unlock),
                date,
                name: val.name,
                planList,
              };
            });

            return {
              name,
              type,
              card,
            };
          });

          resolve(backVal);
        })
        .catch(() => {
          reject();
        });
    });

    // head、studyPlan的数据是同一个接口返回的
    const head = ref({
      cover: '',
      className: '',
      duty: '',
      dateRange: null,
      courseName: '',
    });
    /**
     * @description dom实例
     */
    const study = ref(null);
    const studyPlan = ref({
      series_id: '',
      list: [],
      isMode: 0
    });
    onMounted(() => {
      getDetailsAndPlan(classId).then((res) => {
        head.value = res.head;
        document.title = head.value.className;

        studyPlan.value.series_id = res.series_id;
        studyPlan.value.list = res.list;
        studyPlan.value.isMode = res.isMode;

        // 这两个值都为-1，则没有当前日期的数据，不需要滚动
        if (res.moduleIdx === -1 && res.cardIdx === -1) {
          return;
        }

        // 滚动到当前日期的学习计划
        nextTick(() => {
          let height = 0;

          for (let i = 0; i < res.moduleIdx; i++) {
            const eleHeight = study.value.children[i].scrollHeight + 16;

            height += eleHeight;
          }

          const ele = study.value.children[res.moduleIdx];
          // 开启了模块才加上模块名的高度
          if (studyPlan.value.isMode) {
            if (res.cardIdx >= 0) {
              height += ele.children[0].scrollHeight;
            }
          }

          for (let i = 0; i < res.cardIdx; i++) {
            height += ele.children[i + 1].scrollHeight;
          }

          study.value.parentNode.scrollTop = height;
        });
      });
    });

    return {
      head,
      menu,
      menuCheckedIdx,
      studyPlan,
      study,
      classId,
    };
  },
});
</script>

<style lang="stylus" scoped>
.tab {
  margin-top: 8px;
}

.focus {
  overflow-y: auto;
  margin-top: 0.5px;
  box-sizing: border-box;
  height: calc(100vh - 209px);
  background: #fff;

  .study {
    padding: 20px 16px;
  }
}

.module-name {
  padding: 8px 0 12px 0;
  font-size: 14px;
  color: #999;
}
</style>
